'use client';

// Global
import { SiteName, Brands, Themes } from 'helpers/Constants/Constant';
import { createContext, useContext } from 'react';
import { supportedFonts } from 'lib/fonts';

export const BRAND_MAPPING = {
  BrandsIrwinMitchell: ['IrwinMitchell'],
};

export const ALL_BRANDS = Object.keys(BRAND_MAPPING) as Brands[];

export const THEME_MAPPING: Record<Themes, string[]> = {
  ThemesWhite: ['ThemesWhite'],
  ThemesLight: ['ThemesLight'],
  ThemesDark: ['ThemesDark'],
  ThemesBrandPrimary: ['ThemesBrandPrimary'],
  ThemesBrandSecondary: ['ThemesBrandSecondary'],
};

export const ALL_THEMES = Object.keys(THEME_MAPPING) as Themes[];

export interface BrandAndTheme {
  brand: Brands;
  theme: Themes;
}

export const BrandAndThemeContext = createContext<BrandAndTheme>({
  brand: ALL_BRANDS[0],
  theme: ALL_THEMES[0],
});

export const useBrandAndTheme = () => {
  return useContext(BrandAndThemeContext);
};

export const getBrandForSiteName = (siteName: SiteName) => {
  return Object.entries(BRAND_MAPPING).find(([, siteNames]) =>
    siteNames.includes(siteName)
  )?.[0] as Brands | undefined;
};

type BrandAndThemeProviderProps = {
  children: React.ReactNode;
  /**
   * Optional. The brand to use. Will fallback to the parent brand or the first brand if not provided.
   */
  brand?: Brands;
  /**
   * Optional. The theme to use. Will fallback to the parent theme or the first theme if not provided .
   */
  theme?: Themes;
};

export const BrandAndThemeProvider = ({ children, brand, theme }: BrandAndThemeProviderProps) => {
  const parentBrandAndTheme = useContext(BrandAndThemeContext);
  const brandAndTheme = {
    brand: brand ?? parentBrandAndTheme.brand ?? ALL_BRANDS[0],
    theme: theme ?? parentBrandAndTheme.theme ?? ALL_THEMES[0],
  };

  return (
    <div
      className={`${brandAndTheme.brand} ${brandAndTheme.theme} ${supportedFonts.map((font) => font.variable).join(' ')} brand-root`}
      suppressHydrationWarning
    >
      <BrandAndThemeContext.Provider value={brandAndTheme}>
        {children}
      </BrandAndThemeContext.Provider>
    </div>
  );
};
